<template>
  <div class="modal fade" id="evaluationStartModalForm" tabindex="-1" aria-labelledby="evaluationStartModalFormLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="evaluationStartModalFormLabel">Start Evaluation</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center my-4">
            <div class="spinner-border" role="status">
              <span class="sr-only">&nbsp;&nbsp;Loading...</span>
            </div>
          </div>
          <div v-else>
            <form @submit.prevent="submitForm">
              <div class="mb-3">
                <label for="homework_id" class="form-label">Choose Homework</label>
                <select class="form-control" id="homework_id" v-model="localHomework.id" @change="fetchHomeworkWithSubtasks" required>
                  <option v-for="homework in homeworks" :key="homework.id" :value="homework.id">{{ homework.title }}</option>
                </select>
              </div>

              <div v-if="localHomework.id">
                <div class="form-check mb-3">
                  <input class="form-check-input" type="checkbox" id="hardEvaluation" v-model="hardEvaluation">
                  <label class="form-check-label" for="hardEvaluation">Hard Evaluation</label><br>
                  <small class="form-text text-muted">Hard evaluation = evaluate everything, even already successful submissions</small>
                </div>
                <hr>
                <div class="mb-3">
                  <label class="form-label">Choose Tasks</label>
                  <div v-for="subtask in subtasks.slice().sort((a, b) => a.number - b.number)" :key="subtask.id" class="form-check">
                    <input class="form-check-input" type="checkbox" :id="'subtask-' + subtask.id" :value="subtask.id" v-model="selectedSubtasks">
                    <label class="form-check-label" :for="'subtask-' + subtask.id">{{ subtask.title }}</label>
                  </div>
                </div>
                <hr>
                <div class="form-check mb-3">
                  <input class="form-check-input" type="checkbox" id="manualEvaluation" v-model="manualEvaluation">
                  <label class="form-check-label" for="manualEvaluation">Manual Evaluation</label><br>
                  <small class="form-text text-muted">Manual evaluation = will create new "manual" deadline</small>
                </div>

                <div class="mb-3">
                  <label for="deadline_id" class="form-label">Choose Deadline</label>
                  <select class="form-control" id="deadline_id" v-model="selectedDeadline" :disabled="manualEvaluation">
                    <option v-for="deadline in deadlines" :key="deadline.id" :value="deadline.id">{{ formatDate(deadline.date) }} - {{ getEvaluationTypeLabel(deadline.evaluation_type) }}</option>
                  </select>
                </div>
                <hr>
                <div class="form-check mb-3">
                  <input class="form-check-input" type="checkbox" id="allStudents" v-model="allStudents">
                  <label class="form-check-label" for="allStudents">All Students</label>
                </div>

                <div class="mb-3">
                  <label class="form-label">Enter Student UCOs</label>
                  <input type="text" class="form-control" v-model="studentUcosInput" placeholder="UCOs separated by commas (123456,987645,123654)" :disabled="allStudents">
                </div>
              </div>

              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                <button type="submit" class="btn btn-primary">Submit</button>
              </div> 
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>

  <StemErrorModal ref="stemErrorModal" />
</template>

<script>
  import { Modal } from 'bootstrap';
  import EvaluationController from '@/controllers/EvaluationController';
  import HomeworkController from '@/controllers/HomeworkController';
  import StemErrorModal from '@/components/StemErrorModal.vue';
  import { EvaluationStart } from '@/models/evaluation';
  import Deadline from '@/models/deadline';

  export default {
    name: 'EvaluationStartModalForm',
    components: {
      StemErrorModal
    },
    data() {
      return {
        localHomework: { ...this.homework },
        homeworks: [],
        subtasks: [],
        deadlines: [],
        selectedSubtasks: [],
        selectedDeadline: null,
        studentUcosInput: '',
        allStudents: false,
        manualEvaluation: false,
        hardEvaluation: false,
        loading: true,
      };
    },
    async created() {
      try {
        this.homeworks = await HomeworkController.fetchHomeworks();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    mounted() {
      // Reset = delete all data from the form
      const modalElement = document.getElementById('evaluationStartModalForm');
      modalElement.addEventListener('hidden.bs.modal', this.resetForm);
    },
    methods: {
      resetForm() {
        this.localHomework = { ...this.homework };
        this.subtasks = [];
        this.deadlines = [];
        this.selectedSubtasks = [];
        this.selectedDeadline = null;
        this.studentUcosInput = '';
        this.allStudents = false;
        this.manualEvaluation = false;
        this.hardEvaluation = false;
      },
      formatDate(datetime) {
        return Deadline.formatDateTime(datetime);
      },
      getEvaluationTypeLabel(type) {
        return Deadline.getEvaluationTypeLabel(type);
      },
      async fetchHomeworkWithSubtasks() {
        this.loading = true;
        if (this.localHomework.id) {
          try {
            const homeworkWithRelatedData = await HomeworkController.fetchHomeworkByIdWithRelatedData(this.localHomework.id);
            this.subtasks = homeworkWithRelatedData.subtasks;
            // Sorting deadlines
            this.deadlines = Deadline.sortDeadlinesByDate(homeworkWithRelatedData.deadlines);
          } catch (error) {
            this.$refs.stemErrorModal.show('Error', error.message);
          } finally {
            this.loading = false;
          }
        } else {
          this.loading = false;
        }
      },
      async submitForm() {
        const studentUcos = this.allStudents ? [] : this.studentUcosInput.split(',').map(uco => uco.trim());
        const submissionData = new EvaluationStart({
          homework_id: this.localHomework.id,
          subtask_ids: this.selectedSubtasks,
          student_ucos: studentUcos,
          all_students: this.allStudents,
          deadline_id: this.manualEvaluation ? null : this.selectedDeadline,
          manual_evaluation: this.manualEvaluation,
          hard_evaluation: this.hardEvaluation
        });

        try {
          // Check if we have all required data
          EvaluationStart.validate(submissionData);
          console.log('Submission Data:', submissionData);
          
          // Compormation modal when all students are selected
          if (this.allStudents) {
            const confirmation = confirm('Do you want to start evaluation for all students?');
            if (!confirmation) return;
          }

          // Confirmation modal when hard evaluation is selected
          if (this.hardEvaluation) {
            const confirmation = confirm('Do you want to start hard evaluation? This will re-evaluate everything, even already successful submissions.');
            if (!confirmation) return;
          }

          const response = await EvaluationController.startEvaluation(submissionData);

          if (response && response.length > 0) {
            // If inresponse are all student ucos, show error message
            if (response.length === studentUcos.length) {
              this.$store.dispatch('triggerAlert', {
                type: 'error',
                title: 'Error',
                message: 'Evaluation did not start. All students not found.'
              });
            } else if (response.length > 0) {
              // Show warning message with not found students
              this.$store.dispatch('triggerAlert', {
                type: 'warning',
                title: 'Warning',
                message: 'Evaluation started. Not found students: ' + response.join(', ')
              });
            }
          } else {
            this.$store.dispatch('triggerAlert', {
              type: 'success',
              title: 'Success',
              message: 'Evaluation started successfully'
            });
          }

          // Programmatically hide the modal on success
          const modalElement = document.getElementById('evaluationStartModalForm');
          const modalInstance = Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
        } catch (error) {
          // Show error via the StemErrorModal if validation fails or another error occurs
          this.$refs.stemErrorModal.show('Error', error.message);
        }
      }
    }
  }
</script>